import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import { initials } from '@/admin-area/ui/person-bits';
import { formatDate } from '../../format-date';
import { categoryLabel } from '../../list/expense-labels';
import { formatAmount } from '../../money';
import { listUses, type Page, type Supplier, type SupplierUse } from './api';
import { Card } from './Card';
import { ExpenseLink } from './panel-links';
import { sitesOf } from './SitesCard';

/** Rows per page of «Spese con questo fornitore»; «Mostra altre» asks for the next ones. */
export const USES_PAGE = 10;

/** «2026-09» → «settembre 2026», in the reader's language. */
export function formatMonth(month: string, language: string): string {
  const match = /^(\d{4})-(\d{2})$/.exec(month);
  if (match === null) return month;
  const date = new Date(Date.UTC(Number(match[1]), Number(match[2]) - 1, 1));
  return new Intl.DateTimeFormat(language, {
    month: 'long',
    year: 'numeric',
    timeZone: 'UTC',
  }).format(date);
}

type UsesState =
  | { status: 'loading' }
  | { status: 'error' }
  | { status: 'ready'; items: readonly SupplierUse[]; total: number; more: boolean };

/**
 * «Spese con questo fornitore» (boards 3.1–3.4, 3.7): the expenses that name it, newest first.
 * Who sees what follows §3.10, decided by the API: one's own rows in full, all of them for
 * Gestione and the approvers, the colleagues' as month and type only («un collega», no amount).
 * For an intermediary the site column is «Dove ha dormito».
 */
export function UsesCard({ supplier }: { readonly supplier: Supplier }) {
  const { t, i18n } = useTranslation('suppliers');
  const language = i18n.resolvedLanguage ?? 'it';
  const [state, setState] = useState<UsesState>({ status: 'loading' });
  const [loadingMore, setLoadingMore] = useState(false);

  useEffect(() => {
    let active = true;
    setState({ status: 'loading' });
    void listUses(supplier.id, 0, USES_PAGE).then((page) => {
      if (!active) return;
      setState(page === null ? { status: 'error' } : readyFrom([], page));
    });
    return () => {
      active = false;
    };
  }, [supplier.id]);

  async function more() {
    if (state.status !== 'ready' || loadingMore) return;
    setLoadingMore(true);
    const page = await listUses(supplier.id, state.items.length, USES_PAGE);
    setLoadingMore(false);
    if (page !== null) setState(readyFrom(state.items, page));
  }

  const partial = state.status === 'ready' && state.items.some((use) => !use.full);
  const hint =
    state.status === 'ready'
      ? [
          t('uses.count', { count: state.total }),
          partial ? t('uses.scopePartial') : t('uses.newestFirst'),
        ].join(' · ')
      : undefined;

  return (
    <Card title={t('uses.title')} hint={hint}>
      {state.status === 'loading' ? <Skeleton className="h-24 w-full" /> : null}
      {state.status === 'error' ? (
        <p className="text-muted-foreground text-sm">{t('uses.loadError')}</p>
      ) : null}
      {state.status === 'ready' && state.items.length === 0 ? (
        <p className="text-muted-foreground text-sm">{t('uses.empty')}</p>
      ) : null}
      {state.status === 'ready' && state.items.length > 0 ? (
        <>
          {/* The table or the list by the card's own width, not the shell's: in the side panel a
              form opens, a wide screen still has a narrow card (U1). */}
          <div className="@container/uses">
            <UsesTable supplier={supplier} items={state.items} language={language} />
            <UsesList supplier={supplier} items={state.items} language={language} />
          </div>
          {state.more ? (
            <Button
              variant="outline"
              className="self-start"
              disabled={loadingMore}
              onClick={() => void more()}
            >
              {t('uses.more')}
            </Button>
          ) : null}
        </>
      ) : null}
    </Card>
  );
}

function readyFrom(before: readonly SupplierUse[], page: Page<SupplierUse>): UsesState {
  const items = [...before, ...page.items];
  return { status: 'ready', items, total: page.total, more: items.length < page.total };
}

type T = ReturnType<typeof useTranslation<'suppliers'>>['t'];

/** Where: the supplier's site by its sign; through an intermediary, the property it named. */
function placeOf(use: SupplierUse, supplier: Supplier, t: T): string {
  if (!use.full) return '—';
  const site = sitesOf(supplier).find((candidate) => candidate.id === use.siteId);
  if (supplier.intermediary) {
    // The property belongs to another supplier: the contract gives its id, not its name.
    if (!use.siteId) return t('uses.noProperty');
    return site?.sign ?? t('uses.propertyGiven');
  }
  const name = site ? (site.sign ?? supplier.name) : '—';
  return use.throughIntermediary ? t('uses.throughIntermediary', { site: name }) : name;
}

function When({ use, language }: { readonly use: SupplierUse; readonly language: string }) {
  if (!use.full || !use.date) return <>{formatMonth(use.month, language)}</>;
  const date = formatDate(use.date);
  return use.expenseId ? (
    <ExpenseLink expenseId={use.expenseId} className="text-link font-semibold hover:underline">
      {date}
    </ExpenseLink>
  ) : (
    <>{date}</>
  );
}

/** «Chi» as initials, the full name in the accessible name and on hover (U19). */
function Who({ use }: { readonly use: SupplierUse }) {
  const { t } = useTranslation('suppliers');
  const name = use.full && use.owner ? use.owner.displayName : t('uses.colleague');
  return (
    <span
      role="img"
      aria-label={name}
      title={name}
      className="bg-muted text-muted-foreground inline-grid size-8 place-items-center rounded-full text-xs font-bold"
    >
      {use.full && use.owner ? initials(use.owner.displayName) : '—'}
    </span>
  );
}

function UsesTable({
  supplier,
  items,
  language,
}: {
  readonly supplier: Supplier;
  readonly items: readonly SupplierUse[];
  readonly language: string;
}) {
  const { t } = useTranslation('suppliers');
  return (
    <table className="hidden w-full table-fixed text-sm @min-[620px]/uses:table">
      <colgroup>
        <col className="w-[8.5rem]" />
        <col className="w-14" />
        <col />
        <col className="w-[28%]" />
        <col className="w-28" />
      </colgroup>
      <thead className="text-muted-foreground text-left text-xs">
        <tr>
          <th scope="col" className="py-2 font-semibold">
            {t('uses.columns.date')}
          </th>
          <th scope="col" className="py-2 font-semibold">
            {t('uses.columns.who')}
          </th>
          <th scope="col" className="py-2 font-semibold">
            {t(supplier.intermediary ? 'uses.columns.stay' : 'uses.columns.site')}
          </th>
          <th scope="col" className="py-2 font-semibold">
            {t('uses.columns.category')}
          </th>
          <th scope="col" className="py-2 text-right font-semibold">
            {t('uses.columns.total')}
          </th>
        </tr>
      </thead>
      <tbody className="divide-border divide-y">
        {items.map((use, index) => {
          const place = placeOf(use, supplier, t);
          const category = categoryLabel(use.category, language);
          return (
            <tr key={use.expenseId ?? `row-${index}`}>
              <td className="py-2 whitespace-nowrap tabular-nums">
                <When use={use} language={language} />
              </td>
              <td className="py-2">
                <Who use={use} />
              </td>
              <td className="truncate py-2" title={place}>
                {place}
              </td>
              <td className="truncate py-2" title={category}>
                {category}
              </td>
              <td className="py-2 text-right whitespace-nowrap tabular-nums">
                {use.full && use.total ? formatAmount(use.total) : '—'}
              </td>
            </tr>
          );
        })}
      </tbody>
    </table>
  );
}

/** The phone's form of the same rows (boards 3.6, 3.7): one card each. */
function UsesList({
  supplier,
  items,
  language,
}: {
  readonly supplier: Supplier;
  readonly items: readonly SupplierUse[];
  readonly language: string;
}) {
  const { t } = useTranslation('suppliers');
  return (
    <ul className="divide-border flex flex-col divide-y @min-[620px]/uses:hidden">
      {items.map((use, index) => {
        const who = use.full && use.owner ? use.owner.displayName : t('uses.colleague');
        const place = use.full ? placeOf(use, supplier, t) : null;
        return (
          <li key={use.expenseId ?? `card-${index}`} className="flex flex-col gap-0.5 py-2.5">
            <span className="flex items-baseline justify-between gap-3 text-sm">
              <b className="tabular-nums">
                <When use={use} language={language} />
              </b>
              {use.full && use.total ? (
                <span className="tabular-nums">{formatAmount(use.total)}</span>
              ) : null}
            </span>
            <span className="text-muted-foreground text-xs">
              {[who, place, categoryLabel(use.category, language)].filter(Boolean).join(' · ')}
            </span>
          </li>
        );
      })}
    </ul>
  );
}
